import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import { actorLabel, formatAuditTime } from './auditTable';

const SOURCE_LABEL = {
  organiser: 'Organiser',
  portal: 'Portal',
  client: 'Client',
  system: 'System'
};

const DetailRow = ({ label, children }) => {
  if (children === null || children === undefined || children === '') return null;
  return (
    <Box display="flex" gap={1} alignItems="flex-start">
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ minWidth: 130, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box minWidth={0} flex={1}>
        {typeof children === 'string' || typeof children === 'number' ? (
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {children}
          </Typography>
        ) : (
          children
        )}
      </Box>
    </Box>
  );
};

const JsonBlock = ({ label, value }) => {
  if (value === null || value === undefined) return null;

  let text;
  try {
    text = JSON.stringify(value, null, 2);
  } catch {
    text = String(value);
  }

  if (!text || text === '{}' || text === 'null') return null;

  return (
    <Box mt={1.5}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          fontSize: 12,
          maxHeight: 240,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
        {text}
      </Box>
    </Box>
  );
};

const portalLabel = (scope) => {
  if (scope?.venueName) return scope.venueName;
  if (scope?.kind === 'secretary') return 'Tournament Secretary portal';
  if (scope?.kind === 'club') return 'Club portal';
  return 'This event';
};

const AuditDetailsDialog = ({ open, item, onClose }) => {
  if (!item) {
    return null;
  }

  const isAccess = item.view === 'access';
  const hasBefore = item.before !== null && item.before !== undefined;
  const hasAfter = item.after !== null && item.after !== undefined;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        {item.actionLabel || item.action}
        <Typography variant="body2" color="text.secondary">
          {formatAuditTime(item.createdAt)}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
          {isAccess && (
            <Chip
              size="small"
              color={item.outcome === 'failure' ? 'error' : 'success'}
              label={item.outcome === 'failure' ? 'Failed sign-in' : 'Successful'}
            />
          )}
          <Chip
            size="small"
            variant="outlined"
            label={SOURCE_LABEL[item.source] || item.source || 'Unknown source'}
          />
          <Chip size="small" variant="outlined" label={item.action} />
        </Stack>

        <Stack spacing={1}>
          <DetailRow label="Actor">
            <Typography variant="body2" fontWeight={600}>
              {actorLabel(item.actor)}
            </Typography>
            {item.actor?.email && (
              <Typography variant="body2" color="text.secondary">
                {item.actor.email}
              </Typography>
            )}
          </DetailRow>
          <DetailRow label="Portal">{portalLabel(item.scope)}</DetailRow>
          {item.scope?.eventCount > 1 && (
            <DetailRow label="Shared with">
              {item.scope.eventCount} events
            </DetailRow>
          )}
          {item.scope?.workspaceScope && item.scope.workspaceScope !== 'none' && (
            <DetailRow label="Workspace scope">{item.scope.workspaceScope}</DetailRow>
          )}
          {item.entity && (
            <DetailRow label="Changed item">
              {item.entity.name || item.entity.id || item.entity.type}
              {item.entity.type && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {item.entity.type}
                  {item.entity.id ? ` · ${item.entity.id}` : ''}
                </Typography>
              )}
            </DetailRow>
          )}
          <DetailRow label="Summary">{item.summary}</DetailRow>
          {isAccess && (
            <>
              <Divider sx={{ my: 0.5 }} />
              <DetailRow label="IP address">
                {item.ip
                  ? `${item.ip}${item.ipSource ? ` (${item.ipSource})` : ''}`
                  : 'Not recorded'}
              </DetailRow>
              <DetailRow label="Browser / device">
                {item.userAgent || 'Not recorded'}
              </DetailRow>
              <DetailRow label="Record expires">
                {formatAuditTime(item.expiresAt)}
              </DetailRow>
            </>
          )}
        </Stack>

        <JsonBlock label="Details" value={item.metadata} />

        {(hasBefore || hasAfter) && (
          <Box
            mt={2}
            display="grid"
            gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
            gap={2}>
            {hasBefore && <JsonBlock label="Before" value={item.before} />}
            {hasAfter && <JsonBlock label="After" value={item.after} />}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" display="block" mt={3}>
          Record {item.id}
        </Typography>
      </DialogContent>
    </Dialog>
  );
};

export default AuditDetailsDialog;
